import { graphql } from 'react-relay';
import { useCallback } from 'react';

import { ConfluencePageAri, ConfluenceBlogpostAri } from '@atlassian/ari';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import { CoverPictureQuery } from '../CoverPictureQuery.graphql';

import type {
	useUpdateCoverPictureMutationPageCreateMutation$data,
	useUpdateCoverPictureMutationPageCreateMutation$variables,
} from './__generated__/useUpdateCoverPictureMutationPageCreateMutation.graphql';
import type {
	useUpdateCoverPictureMutationBlogCreateMutation$data,
	useUpdateCoverPictureMutationBlogCreateMutation$variables,
} from './__generated__/useUpdateCoverPictureMutationBlogCreateMutation.graphql';
import type {
	useUpdateCoverPictureMutationPageUpdateMutation$data,
	useUpdateCoverPictureMutationPageUpdateMutation$variables,
} from './__generated__/useUpdateCoverPictureMutationPageUpdateMutation.graphql';
import type {
	useUpdateCoverPictureMutationPageDeleteMutation$data,
	useUpdateCoverPictureMutationPageDeleteMutation$variables,
} from './__generated__/useUpdateCoverPictureMutationPageDeleteMutation.graphql';
import type {
	useUpdateCoverPictureMutationBlogUpdateMutation$data,
	useUpdateCoverPictureMutationBlogUpdateMutation$variables,
} from './__generated__/useUpdateCoverPictureMutationBlogUpdateMutation.graphql';
import type {
	useUpdateCoverPictureMutationBlogDeleteMutation$data,
	useUpdateCoverPictureMutationBlogDeleteMutation$variables,
} from './__generated__/useUpdateCoverPictureMutationBlogDeleteMutation.graphql';

export const useUpdateCoverPictureMutation = ({ contentId }) => {
	const { cloudId } = useSessionData();

	const [createPageCoverPicture] = useMutationInterop<
		useUpdateCoverPictureMutationPageCreateMutation$data,
		useUpdateCoverPictureMutationPageCreateMutation$variables
	>(
		graphql`
			mutation useUpdateCoverPictureMutationPageCreateMutation(
				$propertyKey: String!
				$pageId: ID!
				$propertyValue: String!
			) {
				confluence {
					createPageProperty(input: { key: $propertyKey, pageId: $pageId, value: $propertyValue }) {
						success
						pageProperty {
							key
							value
						}
					}
				}
			}
		`,
		{
			refetchQueries: () => [
				{
					query: CoverPictureQuery,
					variables: {
						contentId,
						status: ['draft'],
						draftShareId: getDraftShareIdFromQueryParam(),
					},
				},
			],
		},
	);

	const [updatePageCoverPicture] = useMutationInterop<
		useUpdateCoverPictureMutationPageUpdateMutation$data,
		useUpdateCoverPictureMutationPageUpdateMutation$variables
	>(
		graphql`
			mutation useUpdateCoverPictureMutationPageUpdateMutation(
				$propertyKey: String!
				$pageId: ID!
				$propertyValue: String!
			) {
				confluence {
					updateValuePageProperty(
						input: { key: $propertyKey, pageId: $pageId, value: $propertyValue }
					) {
						success
						pageProperty {
							key
							value
						}
					}
				}
			}
		`,
		{
			refetchQueries: () => [
				{
					query: CoverPictureQuery,
					variables: {
						contentId,
						status: ['draft'],
						draftShareId: getDraftShareIdFromQueryParam(),
					},
				},
			],
		},
	);

	const [deletePageCoverPicture] = useMutationInterop<
		useUpdateCoverPictureMutationPageDeleteMutation$data,
		useUpdateCoverPictureMutationPageDeleteMutation$variables
	>(
		graphql`
			mutation useUpdateCoverPictureMutationPageDeleteMutation(
				$propertyKey: String!
				$pageId: ID!
			) {
				confluence {
					deletePageProperty(input: { key: $propertyKey, pageId: $pageId }) {
						success
					}
				}
			}
		`,
		{
			refetchQueries: () => [
				{
					query: CoverPictureQuery,
					variables: {
						contentId,
						status: ['draft'],
						draftShareId: getDraftShareIdFromQueryParam(),
					},
				},
			],
		},
	);

	const [createBlogCoverPicture] = useMutationInterop<
		useUpdateCoverPictureMutationBlogCreateMutation$data,
		useUpdateCoverPictureMutationBlogCreateMutation$variables
	>(
		graphql`
			mutation useUpdateCoverPictureMutationBlogCreateMutation(
				$propertyKey: String!
				$blogPostId: ID!
				$propertyValue: String!
			) {
				confluence {
					createBlogPostProperty(
						input: { key: $propertyKey, blogPostId: $blogPostId, value: $propertyValue }
					) {
						success
						blogPostProperty {
							key
							value
						}
					}
				}
			}
		`,
		{
			refetchQueries: () => [
				{
					query: CoverPictureQuery,
					variables: {
						contentId,
						status: ['draft'],
						draftShareId: getDraftShareIdFromQueryParam(),
					},
				},
			],
		},
	);

	const [updateBlogCoverPicture] = useMutationInterop<
		useUpdateCoverPictureMutationBlogUpdateMutation$data,
		useUpdateCoverPictureMutationBlogUpdateMutation$variables
	>(
		graphql`
			mutation useUpdateCoverPictureMutationBlogUpdateMutation(
				$propertyKey: String!
				$blogPostId: ID!
				$propertyValue: String!
			) {
				confluence {
					updateValueBlogPostProperty(
						input: { key: $propertyKey, blogPostId: $blogPostId, value: $propertyValue }
					) {
						success
						blogPostProperty {
							key
							value
						}
					}
				}
			}
		`,
		{
			refetchQueries: () => [
				{
					query: CoverPictureQuery,
					variables: {
						contentId,
						status: ['draft'],
						draftShareId: getDraftShareIdFromQueryParam(),
					},
				},
			],
		},
	);

	const [deleteBlogCoverPicture] = useMutationInterop<
		useUpdateCoverPictureMutationBlogDeleteMutation$data,
		useUpdateCoverPictureMutationBlogDeleteMutation$variables
	>(
		graphql`
			mutation useUpdateCoverPictureMutationBlogDeleteMutation(
				$propertyKey: String!
				$blogPostId: ID!
			) {
				confluence {
					deleteBlogPostProperty(input: { key: $propertyKey, blogPostId: $blogPostId }) {
						success
					}
				}
			}
		`,
		{
			refetchQueries: () => [
				{
					query: CoverPictureQuery,
					variables: {
						contentId,
						status: ['draft'],
						draftShareId: getDraftShareIdFromQueryParam(),
					},
				},
			],
		},
	);

	const updateCoverPictureMutation = useCallback(
		({
			propertyKey,
			propertyValue: propertyValueString,
			contentType,
			isDelete,
			hasCoverPicture,
		}: {
			propertyKey: string;
			propertyValue?: string;
			contentType: string;
			isDelete: boolean;
			hasCoverPicture: boolean;
		}) => {
			if (!isDelete && !!propertyValueString) {
				// Property value need to be stringified again before passing to graphql otherwise backend cannot parse it correctly
				const propertyValue = JSON.stringify(propertyValueString);
				if (hasCoverPicture) {
					if (contentType === 'page') {
						return updatePageCoverPicture({
							variables: {
								pageId: ConfluencePageAri.create({
									siteId: cloudId,
									pageId: contentId,
								}).toString(),
								propertyKey,
								propertyValue,
							},
						});
					} else if (contentType === 'blogpost') {
						return updateBlogCoverPicture({
							variables: {
								blogPostId: ConfluenceBlogpostAri.create({
									siteId: cloudId,
									blogpostId: contentId,
								}).toString(),
								propertyKey,
								propertyValue,
							},
						});
					}
				} else {
					if (contentType === 'page') {
						return createPageCoverPicture({
							variables: {
								pageId: ConfluencePageAri.create({
									siteId: cloudId,
									pageId: contentId,
								}).toString(),
								propertyKey,
								propertyValue,
							},
						});
					} else if (contentType === 'blogpost') {
						return createBlogCoverPicture({
							variables: {
								blogPostId: ConfluenceBlogpostAri.create({
									siteId: cloudId,
									blogpostId: contentId,
								}).toString(),
								propertyKey,
								propertyValue,
							},
						});
					}
				}
			}
			if (isDelete) {
				if (contentType === 'page') {
					return deletePageCoverPicture({
						variables: {
							pageId: ConfluencePageAri.create({
								siteId: cloudId,
								pageId: contentId,
							}).toString(),
							propertyKey,
						},
					});
				} else if (contentType === 'blogpost') {
					return deleteBlogCoverPicture({
						variables: {
							blogPostId: ConfluenceBlogpostAri.create({
								siteId: cloudId,
								blogpostId: contentId,
							}).toString(),
							propertyKey,
						},
					});
				}
			}
		},
		[
			createPageCoverPicture,
			createBlogCoverPicture,
			updatePageCoverPicture,
			updateBlogCoverPicture,
			deletePageCoverPicture,
			deleteBlogCoverPicture,
			contentId,
			cloudId,
		],
	);

	return { updateCoverPictureMutation };
};
